import React, { useEffect, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { toast } from "react-toastify";

import palette from "../../../common/styles/palette";
import SheAiChecklistService from "../../../services/sheai/SheAiChecklistService";
import {
  AiAnalysis,
  AiMedicalReview,
  AiMedicalReviewItem,
  AiSuggestedVerdict,
} from "../../../services/sheai/SheAiChecklistTypes";
import {
  AiBlocoTitulo,
  AiCallout,
  AiCard,
  AiFichaLinha,
  AiPill,
  AiSectionHeader,
  Tom,
} from "./aiSurface";

/**
 * A sub-aba "Parecer médico" da aba Dados IA.
 *
 * O agente de vídeo (Gemini) disse o que o aluno fez. Aqui um segundo modelo,
 * médico e SEM acesso ao vídeo, lê essa análise e responde se cada veredito é
 * clinicamente sustentável diante da evidência citada, o que a estação deveria
 * ter cobrado e o que ensinar no debriefing.
 *
 * É opinião, nunca nota: nada aqui altera veredito, pontuação ou o feedback do
 * professor. E é chaveado pela rodada — reprocessou o vídeo, o parecer antigo
 * some e a tela oferece gerar outro sobre a análise nova.
 */
interface Props {
  debriefingId: string | number;
  analise?: AiAnalysis | null;
  /** Enunciado por `item_id`, para o parecer não falar em «item 214». */
  perguntaPorItem?: Record<string, string>;
}

const ROTULO_ACORDO: Record<AiMedicalReviewItem["agreement"], { label: string; tom: Tom }> = {
  concorda: { label: "Concorda com a IA", tom: "sucesso" },
  discorda: { label: "Discorda da IA", tom: "perigo" },
  indeterminado: { label: "Indeterminado sem o vídeo", tom: "neutro" },
};

const ROTULO_VEREDITO: Record<string, string> = {
  done: "Fez",
  partially_done: "Fez parcialmente",
  done_wrong: "Fez errado",
  not_done: "Não fez",
};

/** Mesmo brilho/fade do painel de autoria: output do MedGemma acende em verde. */
const brilhoMedgemma = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0, 153, 102, 0.40); }
  60%  { box-shadow: 0 0 0 10px rgba(0, 153, 102, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 153, 102, 0); }
`;

const surgirMedgemma = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ChecklistMedicalReviewSection: React.FC<Props> = ({
  debriefingId,
  analise,
  perguntaPorItem,
}) => {
  const [review, setReview] = useState<AiMedicalReview | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [decorrido, setDecorrido] = useState(0);
  const [steer, setSteer] = useState("");
  // Vereditos já gravados a partir do parecer nesta sessão, por item.
  const [aplicados, setAplicados] = useState<Record<string, AiSuggestedVerdict>>({});
  const [adotando, setAdotando] = useState<Record<string, boolean>>({});
  const cronometro = useRef<number | null>(null);

  const analysisId = analise?.analysis_ts ?? null;

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    SheAiChecklistService.getMedicalReview(debriefingId)
      .then(({ data }) => {
        if (!vivo) return;
        setEnabled(data.enabled);
        setReview(data.review);
      })
      .catch(() => {
        if (vivo) setReview(null);
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
    // Reconsulta quando a rodada troca: o parecer é da rodada, não do debriefing.
  }, [debriefingId, analysisId]);

  useEffect(() => {
    if (gerando) {
      const inicio = Date.now();
      setDecorrido(0);
      cronometro.current = window.setInterval(
        () => setDecorrido(Math.round((Date.now() - inicio) / 1000)),
        1000
      );
    } else if (cronometro.current !== null) {
      window.clearInterval(cronometro.current);
      cronometro.current = null;
    }
    return () => {
      if (cronometro.current !== null) window.clearInterval(cronometro.current);
    };
  }, [gerando]);

  const gerar = async () => {
    setGerando(true);
    try {
      const { data } = await SheAiChecklistService.postMedicalReview(
        debriefingId,
        steer.trim() || undefined
      );
      setReview(data.review);
      toast.success("Parecer médico gerado.");
    } catch (e: unknown) {
      const resposta = e as { response?: { data?: { msg?: string } } };
      toast.error(
        resposta?.response?.data?.msg || "O revisor médico não respondeu. Tente novamente."
      );
    } finally {
      setGerando(false);
    }
  };

  /**
   * Pré-preenche o Veredito do professor com a sugestão do parecer — só onde o
   * MedGemma DISCORDOU e disse qual veredito a evidência sustenta. Grava pelo
   * mesmo caminho da aba Checklist (`debriefing/scales`); o clique é o
   * consentimento. O que já foi aplicado nesta sessão fica marcado no cartão.
   */
  const adotarVeredito = async (itemId: string) => {
    setAdotando((atual) => ({ ...atual, [itemId]: true }));
    try {
      const { data } = await SheAiChecklistService.adoptMedicalReviewVerdict(debriefingId, itemId);
      setAplicados((atual) => ({ ...atual, [itemId]: data.scale }));
      toast.success(`Veredito «${ROTULO_VEREDITO[data.scale]}» gravado na Correção.`);
    } catch (e: unknown) {
      const resposta = e as { response?: { data?: { msg?: string } } };
      toast.error(resposta?.response?.data?.msg || "Não foi possível gravar o veredito.");
    } finally {
      setAdotando((atual) => ({ ...atual, [itemId]: false }));
    }
  };

  const sugeridosPendentes = (review?.review.items || []).filter(
    (item) => item.agreement === "discorda" && item.suggested_verdict && !aplicados[item.item_id]
  );

  const adotarTodos = async () => {
    for (const item of sugeridosPendentes) {
      // Sequencial de propósito: cada gravação é um turno em `debriefing/scales`
      // e um toast por item conta ao professor exatamente o que entrou.
      // eslint-disable-next-line no-await-in-loop
      await adotarVeredito(item.item_id);
    }
  };

  const enunciado = (itemId: string) => {
    const doChecklist = perguntaPorItem?.[itemId];
    if (doChecklist) return doChecklist;
    const daAnalise = (analise?.items || []).find((i) => String(i.item_id) === itemId);
    return daAnalise?.question?.replace(/^\[[^\]]+\]\s*/, "") || `Item ${itemId}`;
  };

  const vereditoIa = (itemId: string) => {
    const item = (analise?.items || []).find((i) => String(i.item_id) === itemId);
    return item ? ROTULO_VEREDITO[item.result] ?? item.result : "—";
  };

  const semAnalise = !analise || !(analise.items && analise.items.length > 0);

  if (carregando) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!enabled) {
    return (
      <AiCallout tom="neutro" titulo="Revisor médico não habilitado neste ambiente">
        O parecer médico-acadêmico depende do MedGemma configurado no servidor.
      </AiCallout>
    );
  }

  const minutos = Math.floor(decorrido / 60);
  const segundos = String(decorrido % 60).padStart(2, "0");

  return (
    <Box sx={{ display: "grid", gap: 3, mt: 1 }}>
      <AiCard
        sx={{
          borderLeft: `4px solid ${palette.emeraldDeep}`,
          animation: `${surgirMedgemma} 360ms ease-out, ${brilhoMedgemma} 1.4s ease-out 360ms`,
        }}
      >
        <AiSectionHeader
          icone={<LocalHospitalOutlinedIcon sx={{ color: palette.emeraldDeep }} />}
          titulo="Parecer médico-acadêmico"
          subtitulo={
            <>
              Um segundo modelo, médico e sem acesso ao vídeo, lê a análise da SheAI e
              responde se cada veredito é clinicamente sustentável diante da evidência
              citada, o que a estação deveria ter cobrado e o que ensinar no debriefing.
              É opinião para o professor ler antes de decidir — não altera veredito,
              nota nem o seu feedback. Onde o MedGemma discorda e sugere outro
              veredito, você pode pré-preencher a Correção com um clique.
            </>
          }
          acao={
            review && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <AiPill label={review.model} tom="sucesso" />
                {sugeridosPendentes.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckCircleOutlineIcon />}
                    disabled={gerando || Object.values(adotando).some(Boolean)}
                    onClick={adotarTodos}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      backgroundColor: palette.emeraldDeep,
                      "&:hover": { backgroundColor: palette.emeraldDeep },
                    }}
                  >
                    Aplicar {sugeridosPendentes.length}{" "}
                    {sugeridosPendentes.length === 1 ? "veredito sugerido" : "vereditos sugeridos"}
                  </Button>
                )}
              </Box>
            )
          }
        />

        {semAnalise && (
          <AiCallout tom="neutro" sx={{ mt: 2 }}>
            O parecer é sobre a análise de vídeo concluída. Quando ela existir, o botão
            aparece aqui.
          </AiCallout>
        )}

        {!semAnalise && !review && !gerando && (
          <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
            <TextField
              multiline
              minRows={2}
              fullWidth
              size="small"
              label="Ênfase para o revisor (opcional)"
              placeholder="Ex.: red flags do quadro, ordem de prioridade das condutas, contraindicações, diretriz da sociedade de especialidade."
              value={steer}
              onChange={(ev) => setSteer(ev.target.value)}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<LocalHospitalOutlinedIcon />}
                onClick={gerar}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  backgroundColor: palette.emeraldDeep,
                  "&:hover": { backgroundColor: palette.emeraldStrong },
                }}
              >
                Pedir parecer médico
              </Button>
              <Typography variant="caption" sx={{ color: palette.textMuted }}>
                A GPU do revisor é ligada sob demanda: a primeira chamada depois de um
                período parado pode levar vários minutos. Pode deixar a aba aberta.
              </Typography>
            </Box>
          </Box>
        )}

        {gerando && (
          <Box
            role="status"
            aria-live="polite"
            sx={{ mt: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}
          >
            <CircularProgress size={20} sx={{ color: palette.emeraldDeep }} />
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: palette.text }}>
                O MedGemma está lendo a análise… {minutos}:{segundos}
              </Typography>
              <Typography variant="caption" sx={{ color: palette.textMuted }}>
                Ligando a GPU e carregando o modelo quando necessário; depois disso a
                leitura leva cerca de um a dois minutos.
              </Typography>
            </Box>
          </Box>
        )}
      </AiCard>

      {review && !gerando && (
        <>
          <AiCard>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              <AiPill label="Conhecimento do modelo — não verificado" tom="atencao" />
              <AiPill
                label={`Gerado em ${new Date(review.generated_at).toLocaleString("pt-BR")}`}
                tom="neutro"
              />
              {typeof review.elapsed_s === "number" && (
                <AiPill label={`${Math.round(review.elapsed_s)} s de leitura`} tom="neutro" />
              )}
            </Box>

            {review.review.summary && (
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: palette.text, mb: 1 }}>
                {review.review.summary}
              </Typography>
            )}
            {review.review.overall_assessment && (
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: palette.textMuted }}>
                {review.review.overall_assessment}
              </Typography>
            )}
            {review.steer && (
              <Box sx={{ mt: 1.5 }}>
                <AiFichaLinha rotulo="Ênfase pedida">{review.steer}</AiFichaLinha>
              </Box>
            )}
          </AiCard>

          {review.review.items.length > 0 && (
            <AiCard>
              <AiBlocoTitulo>Leitura item a item</AiBlocoTitulo>
              <Box sx={{ display: "grid", gap: 1.5 }}>
                {review.review.items.map((item) => {
                  const acordo = ROTULO_ACORDO[item.agreement];
                  return (
                    <Box
                      key={item.item_id}
                      sx={{
                        p: 2,
                        borderRadius: `${palette.radius}px`,
                        border: `1px solid ${palette.border}`,
                        borderLeft: `4px solid ${
                          item.agreement === "discorda"
                            ? palette.dangerBorder
                            : item.agreement === "concorda"
                            ? palette.emeraldStrong
                            : palette.border
                        }`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: palette.text, flex: 1 }}>
                          {enunciado(item.item_id)}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                          <AiPill label={`IA: ${vereditoIa(item.item_id)}`} tom="marca" />
                          <AiPill label={acordo.label} tom={acordo.tom} solido={item.agreement === "discorda"} />
                          {aplicados[item.item_id] ? (
                            <AiPill
                              label={`Veredito gravado: ${ROTULO_VEREDITO[aplicados[item.item_id]]}`}
                              tom="sucesso"
                              solido
                            />
                          ) : (
                            item.agreement === "discorda" &&
                            item.suggested_verdict && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                  adotando[item.item_id] ? (
                                    <CircularProgress size={14} sx={{ color: palette.emeraldDeep }} />
                                  ) : (
                                    <CheckCircleOutlineIcon />
                                  )
                                }
                                disabled={Boolean(adotando[item.item_id])}
                                onClick={() => adotarVeredito(item.item_id)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  color: palette.emeraldDeep,
                                  borderColor: palette.emeraldDeep,
                                }}
                              >
                                Aplicar «{ROTULO_VEREDITO[item.suggested_verdict]}» na Correção
                              </Button>
                            )
                          )}
                        </Box>
                      </Box>

                      {item.clinical_rationale && (
                        <Typography sx={{ mt: 1, fontSize: 13, lineHeight: 1.6, color: palette.text }}>
                          {item.clinical_rationale}
                        </Typography>
                      )}

                      {item.teaching_point && (
                        <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "flex-start" }}>
                          <SchoolOutlinedIcon sx={{ fontSize: 18, color: palette.emeraldDeep, mt: 0.1 }} />
                          <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: palette.textMuted }}>
                            <strong style={{ color: palette.emeraldDeep }}>Ponto de ensino: </strong>
                            {item.teaching_point}
                          </Typography>
                        </Box>
                      )}

                      {item.safety_flags.length > 0 && (
                        <Box sx={{ mt: 1, display: "grid", gap: 0.5 }}>
                          {item.safety_flags.map((flag, i) => (
                            <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                              <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: palette.dangerText, mt: 0.2 }} />
                              <Typography sx={{ fontSize: 12.5, color: palette.dangerText }}>{flag}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {item.guidelines.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          {item.guidelines.map((g, i) => (
                            <Typography key={i} sx={{ fontSize: 12, color: palette.textMuted }}>
                              {[g.organization, g.reference, g.year, g.recommendation]
                                .filter((p) => p !== undefined && p !== "")
                                .join(" · ")}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </AiCard>
          )}

          {(review.review.missing_in_station.length > 0 ||
            review.review.academic_recommendations.length > 0) && (
            <Box
              sx={{
                display: "grid",
                gap: 3,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              }}
            >
              {review.review.missing_in_station.length > 0 && (
                <AiCard>
                  <AiBlocoTitulo>O que a estação não cobrou</AiBlocoTitulo>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 0.75 }}>
                    {review.review.missing_in_station.map((t, i) => (
                      <Typography component="li" key={i} sx={{ fontSize: 13, color: palette.text }}>
                        {t}
                      </Typography>
                    ))}
                  </Box>
                </AiCard>
              )}
              {review.review.academic_recommendations.length > 0 && (
                <AiCard>
                  <AiBlocoTitulo>Recomendações para o debriefing</AiBlocoTitulo>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 0.75 }}>
                    {review.review.academic_recommendations.map((t, i) => (
                      <Typography component="li" key={i} sx={{ fontSize: 13, color: palette.text }}>
                        {t}
                      </Typography>
                    ))}
                  </Box>
                </AiCard>
              )}
            </Box>
          )}

          {review.review.ontologies.length > 0 && (
            <AiCard>
              <AiBlocoTitulo>Terminologia</AiBlocoTitulo>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {review.review.ontologies.map((o, i) => (
                  <Chip
                    key={i}
                    size="small"
                    variant="outlined"
                    label={[o.system, o.code, o.term].filter(Boolean).join(" · ")}
                    sx={{ fontSize: 11.5 }}
                  />
                ))}
              </Box>
            </AiCard>
          )}

          {/* Follow-up: uma ênfase e pedir de novo sem sair da aba. O parecer
              novo substitui o atual (é da mesma rodada de análise). */}
          <AiCard sx={{ borderLeft: `4px solid ${palette.emeraldDeep}` }}>
            <AiBlocoTitulo>Refinar o parecer</AiBlocoTitulo>
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <TextField
                multiline
                minRows={2}
                fullWidth
                size="small"
                label="Ênfase para o revisor (opcional)"
                placeholder="Ex.: reveja só os itens em que discordou; considere a diretriz AHA 2020; foque na segurança do paciente."
                value={steer}
                onChange={(ev) => setSteer(ev.target.value)}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={gerar}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    color: palette.emeraldDeep,
                    borderColor: palette.emeraldDeep,
                  }}
                >
                  Reprocessar com esta ênfase
                </Button>
                <Typography variant="caption" sx={{ color: palette.textMuted }}>
                  Substitui o parecer atual. Vereditos já aplicados na Correção não mudam.
                </Typography>
              </Box>
            </Box>
          </AiCard>
        </>
      )}
    </Box>
  );
};

export default ChecklistMedicalReviewSection;
